import type { FlowReport } from '../../../app-core/lib/flow/FlowReport'
import type { WorkflowReference } from '../../../shared/domain/workflows'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { isNotNil } from '../../../shared/lib/functional/nil.no-deps'
import { Flow_syncOngoingReport } from './Flow.syncOngoingReports'

type WithChildren<T> = T & { children: T[] }

/** If there's a race conditon and the subtask's parent is completed first,
 * then the parent report finalization will fail, resulting in a weird state in the ui,
 * making it impossible to complete it */
export function syncReportsStartingFromLeavesToParents(
    reports: Array<FlowReport<WorkflowReference>>,
    logger: ClientLogger
) {
    const allReportsWithChildren = reports.map(withChildrenDeep(reports)).flat(Infinity)
    const leafFlows = allReportsWithChildren.filter(({ children }) => !children.length)
    return syncRecursive(leafFlows, allReportsWithChildren, logger)
}

async function syncRecursive(
    leafFlows: Array<WithChildren<FlowReport<WorkflowReference>>>,
    allReportsWithChildren: Array<WithChildren<FlowReport<WorkflowReference>>>,
    logger: ClientLogger
): Promise<void> {
    if (!leafFlows.length) {
        return
    }
    await syncFlows(leafFlows, logger)
    const parentIds = new Set(leafFlows.flatMap((flow) => flow.parentClientId).filter(isNotNil))
    const parentFlows = allReportsWithChildren.filter(({ id }) => parentIds.has(id))
    return syncRecursive(parentFlows, allReportsWithChildren, logger)
}

async function syncFlows(leafFlows: Array<WithChildren<FlowReport<WorkflowReference>>>, logger: ClientLogger) {
    await Promise.all(leafFlows.map((flow) => Flow_syncOngoingReport(logger, flow.id).catch(logger.error)))
}

function withChildrenDeep(
    reports: Array<FlowReport<WorkflowReference>>
): (parentReport: FlowReport<WorkflowReference>) => WithChildren<FlowReport<WorkflowReference>> {
    return (parentReport: FlowReport<WorkflowReference>) => ({
        ...parentReport,
        children: reports.filter((report) => report.parentClientId === parentReport.id),
    })
}
